import React from 'react';
import Search from '@homeaway/react-searchbox';
import {ButtonPrimary} from '@homeaway/react-buttons';
import {toURL} from '@homeaway/search-url-parser';
import edap from '../../edap';
import {withTranslationsAsProps} from '../../../components/translations';
import {FormattedHTMLMessage} from 'react-intl';
import {compose} from 'redux';
import {connect} from 'react-redux';
import actions from '../../../actions/actions';
import determineIfMobile from '@homeaway/react-determine-if-mobile';

const isDateRangeAfterToday = (startDate, endDate) => {
    if (!startDate || !endDate) {
        return false;
    }
    const q = new Date();
    const m = q.getMonth();
    const d = q.getDate();
    const y = q.getFullYear();

    let currentDate = new Date(y, m, d);
    currentDate.setHours(0, 0, 0, 0);
    currentDate = currentDate.getTime();

    let dateCheckIn = new Date(startDate);
    let dateCheckOut = new Date(endDate);

    if (currentDate >= dateCheckIn.getTime() || currentDate >= dateCheckOut.getTime()) {
        return false;
    }
    return true;
};

export const submitSearch = (role, siteName, boardId, updateTripData, edapActionLocation) => (search) => {
    const {
        checkIn,
        checkOut,
        destination = '',
        subDestination,
        guests: {adultsCount, childrenCount = 0, petIncluded = false}
    } = search;

    const dated = checkIn && checkOut;

    edap.tripboardSearch(boardId, role, dated, edapActionLocation);

    const filterId = subDestination && subDestination.filter && subDestination.filter.id;

    const url = toURL(
        {
            arrival: checkIn,
            departure: checkOut,
            q: destination,
            adultsCount,
            childrenCount,
            petIncluded,
            filter: filterId ? [`filter:${filterId}`] : []
        }
    );
    const tripData = {
        searchUrl: url,
        location: destination,
        adultsCount,
        childrenCount,
        petIncluded,
        arrival: checkIn,
        departure: checkOut
    };
    updateTripData(tripData);

    window.open(url, '_blank');
};

export const SearchBoxWrapper = (props) => {
    const {
        adultLabel,
        adultsLabel,
        applyLabel,
        arriveLabel,
        dateCheckIn,
        dateCheckOut,
        boardId,
        childLabel,
        childrenLabel,
        clearLabel,
        departLabel,
        destination,
        doneLabel,
        edapActionLocation,
        guestLabel,
        guestContinueLabel,
        guestTitle,
        guestsLabel,
        helpLabel,
        isMobile,
        locale,
        noLabel,
        petsLabel,
        petMessage,
        role,
        searchTitle,
        searchTextTitle,
        searchTextDesktop,
        searchTextMobile,
        siteName,
        stayGuests,
        trip,
        typeaheadLabel,
        updateTripData,
        yesLabel
    } = props;

    const labels = {
        buttonTitle: searchTitle,
        mobileTitle: searchTitle,
        datePicker: {
            clear: clearLabel,
            label: arriveLabel,
            label2: departLabel,
            done: doneLabel,
            help: helpLabel
        },
        guests: {
            label: guestTitle,
            adult: adultLabel,
            adults: adultsLabel,
            child: childLabel,
            children: childrenLabel,
            pets: petsLabel,
            guest: guestLabel,
            guests: guestsLabel,
            yesBtn: yesLabel,
            noBtn: noLabel,
            clearBtn: clearLabel,
            applyBtn: applyLabel,
            modalTitle: guestTitle,
            mobileBtn: guestContinueLabel,
            petMessage
        },
        typeahead: {
            destinationplaceholdermsg: typeaheadLabel
        }
    };
    const headerTextId = isMobile ? searchTextMobile : searchTextDesktop;
    const buttonComponent = () => {
        return <ButtonPrimary type="submit" label={searchTitle} size="lg" />;
    };

    const resolvedDestination = destination || (trip && trip.location) || '';
    return (
        <div className="trip-board-details__search-box">
            <div className="search-box__copy">
                <div className="search-box__title">
                    <FormattedHTMLMessage id={searchTextTitle} description="search box title" defaultMessage={''} />
                </div>
                {headerTextId && (
                    <div className="search-box__text">
                        <FormattedHTMLMessage id={headerTextId} description="search box text" defaultMessage={''} />
                    </div>
                )}
            </div>
            <Search
                actionLocation={edapActionLocation}
                checkIn={dateCheckIn}
                checkOut={dateCheckOut}
                consolidateDateInputs={false}
                destination={resolvedDestination}
                guests={stayGuests}
                geoType={'point_of_interest'}
                isMobile={isMobile}
                labels={labels}
                locale={locale}
                buttonComponent={buttonComponent()}
                onSubmit={submitSearch(role, siteName, boardId, updateTripData, edapActionLocation)}
                splitDatePicker
                useMobileExpanded
                typeaheadProps={{isTypeaheadInputFocus: false}}
            />
        </div>
    );
};

const mapStateToProps = ({trip}, {arrival, departure, guests, preferStayProps}) => {
    let cookieTrip = trip || {};
    let dateCheckIn = cookieTrip.arrival;
    let dateCheckOut = cookieTrip.departure;
    let stayGuests = {
        adultsCount: cookieTrip.adultsCount,
        childrenCount: cookieTrip.childrenCount,
        petIncluded: cookieTrip.petIncluded
    };
    const usePropDates = preferStayProps || !dateCheckIn || !dateCheckOut;
    const usePropGuests = preferStayProps || (!cookieTrip.adultsCount && cookieTrip.adultsCount !== 0);
    if (usePropDates && arrival && departure) {
        // we want to use the passed in props in preference over cookies
        dateCheckIn = arrival;
        dateCheckOut = departure;
    }

    if (usePropGuests) {
        stayGuests = guests;
    }

    if (!isDateRangeAfterToday(dateCheckIn, dateCheckOut)) {
        dateCheckIn = null;
        dateCheckOut = null;
    }

    return {
        trip,
        dateCheckIn,
        dateCheckOut,
        stayGuests
    };
};

const mapDispatchToProps = {
    updateTripData: actions.updateTripData
};

export default compose(
    withTranslationsAsProps({
        'datepicker.dates.label': 'dateLabel',
        'datepicker.clear': 'clearLabel',
        'datepicker.done': 'doneLabel',
        'datepicker.help': 'helpLabel',
        'guest.selector.title': 'guestTitle',
        'guest.selector.adult': 'adultLabel',
        'guest.selector.adults': 'adultsLabel',
        'guest.selector.child': 'childLabel',
        'guest.selector.children': 'childrenLabel',
        'guest.selector.pets': 'petsLabel',
        'guest.selector.guest': 'guestLabel',
        'guest.selector.guests': 'guestsLabel',
        'guest.selector.pets.yes': 'yesLabel',
        'guest.selector.pets.no': 'noLabel',
        'guest.selector.clear': 'clearLabel',
        'guest.selector.apply': 'applyLabel',
        'guest.modal.title': 'modalTitle',
        'guest.modal.continue': 'guestContinueLabel',
        'guest.modal.petmessage': 'petMessage',
        'typeahead.error': 'errorMessage',
        'typeahead.label': 'typeaheadLabel',
        'typeahead.message': 'typeaheadMessage',
        'search.button': 'buttonLabel',
        'search.modal.title': 'searchTitle',
        arrive: 'arriveLabel',
        depart: 'departLabel'
    }),
    determineIfMobile,
    connect(mapStateToProps, mapDispatchToProps)
)(SearchBoxWrapper);



// WEBPACK FOOTER //
// ./src/universal/tripboard/details/components/search-box-wrapper.js